import React, { useState, useEffect, useCallback } from "react";
import ReactImageZoom from "react-image-zoom";
import dayjs from '../../lib/day';
import { Loading, Button, Text, Row, Card, Divider, Textarea, Input } from '@nextui-org/react';
import { TickSquare, CloseSquare, PaperDownload, Danger, Chat } from 'react-iconly';

import { secureFetch, fetchWithJson } from "../../lib/api";
import { isNumber } from "../../lib/utils";
import { error, success, warning } from "../../lib/userAlerts";
import endpoint from "../../config/endpoint.json";

const RECEIPT_STATUS = {
  APPROVED: "Approved",
  REJECTED: "Rejected",
  RESET: "New",
  SAVED: "Saved"
};

const MatchIndicator = ({ isMatch }) => (
  <div className="flex justify-center">
    {isMatch === "Y" ? (
      <TickSquare set='light' color='green' />
    ) : (
      <Danger set='light' color='red' />
    )}
  </div>
);

const DataComparisonTable = ({ receiptsDetails }) => (
  <div className="overflow-x-auto">
    <table className="table w-full">
      <thead>
        <tr className="bg-base-200">
          <th className="px-6 py-4 text-left text-base font-semibold">Field</th>
          <th className="px-6 py-4 text-left text-base font-semibold">OCR Data</th>
          <th className="px-6 py-4 text-left text-base font-semibold">Driver Data</th>
          <th className="px-6 py-4 text-center text-base font-semibold">Match Status</th>
        </tr>
      </thead>
      <tbody>
        <tr className="hover:bg-base-100">
          <td className="px-6 py-4 text-base">Date</td>
          <td className="px-6 py-4 text-base">{receiptsDetails.ocr_date}</td>
          <td className="px-6 py-4 text-base">{receiptsDetails.employee_date}</td>
          <td className="px-6 py-4 text-center">
            <MatchIndicator isMatch={receiptsDetails.date_match} />
          </td>
        </tr>
        <tr className="hover:bg-base-100">
          <td className="px-6 py-4 text-base">Total Amount</td>
          <td className="px-6 py-4 text-base">$ {receiptsDetails.ocr_total}</td>
          <td className="px-6 py-4 text-base">$ {receiptsDetails.employee_total}</td>
          <td className="px-6 py-4 text-center">
            <MatchIndicator isMatch={receiptsDetails.total_match} />
          </td>
        </tr>
        <tr className="hover:bg-base-100">
          <td className="px-6 py-4 text-base">Confidence Level</td>
          <td className="px-6 py-4 text-base">{receiptsDetails.ocr_confidence}</td>
          <td className="px-6 py-4 text-base"></td>
          <td className="px-6 py-4 text-center"></td>
        </tr>
      </tbody>
    </table>
  </div>
);

const ActionButtons = ({ 
  onApprove, 
  onReject, 
  onReset, 
  onSave, 
  onDownload, 
  onClose 
}) => (
  <div className="flex flex-wrap gap-2 justify-end mt-4">
    <button
      className="btn btn-success btn-sm gap-2 text-white"
      onClick={onApprove}
    >
      <TickSquare set='light' />
      Save & Approve
    </button>
    
    <button
      className="btn btn-error btn-sm gap-2 text-white"
      onClick={onReject}
    >
      <Danger set='light' />
      Save & Reject
    </button>
    
    <button
      className="btn btn-ghost btn-sm gap-2"
      onClick={onReset}
    >
      <CloseSquare set='light' />
      Reset status
    </button>
    
    <button
      className="btn btn-ghost btn-sm gap-2"
      onClick={onSave}
    >
      <Chat set='light' />
      Save Comments
    </button>
    
    <button
      className="btn btn-ghost btn-sm gap-2"
      onClick={onDownload}
    >
      <PaperDownload set='light' />
      Download Receipt
    </button>
    
    <button
      className="btn btn-ghost btn-sm gap-2"
      onClick={onClose}
    >
      Close
    </button>
  </div>
);

const ReimbursableReceiptsDetails = ({ currentReceipt, closeAction, setStatusOnGrid, resetFilter }) => {
  const [state, setState] = useState({
    receiptsDetails: {},
    amount: 0,
    date: "",
    adoptOCR: false,
    adoptDriver: false,
    receiptUrl: "",
    localUrl: "",
    fetching: false,
    imageLoading: false,
    imageBlob: null,
    comment: ""
  });

  const getZoomProps = useCallback(() => ({
    width: 400,
    height: 500,
    zIndex: 999,
    offset: { vertical: 0, horizontal: -1000 },
    zoomWidth: 500,
    img: state.localUrl
  }), [state.localUrl]);

  const handleInputChange = useCallback((field, value) => {
    if (field === 'amount' && !isNumber(value)) return;
    setState(prev => ({ ...prev, [field]: value }));
  }, []);

  const fetchImage = useCallback(async (preSignedUrl) => {
    setState(prev => ({ ...prev, imageLoading: true }));
    
    try {
      const response = await fetch(preSignedUrl, {
        mode: "cors",
        header: { "Access-Control-Allow-Origin": "*" }
      });
      const blob = await response.blob();
      const urlCreator = window.URL || window.webkitURL;
      const imageUrl = urlCreator.createObjectURL(blob);
      
      setState(prev => ({ 
        ...prev, 
        localUrl: imageUrl, 
        imageBlob: blob,
        imageLoading: false
      }));
    } catch (err) {
      error("Failed to download receipt image");
      setState(prev => ({ ...prev, imageLoading: false }));
    }
  }, []);

  const fetchReceiptImage = useCallback(async (fileKey) => {
    if (!fileKey) {
      console.warn("No file key available for image fetch");
      return;
    }

    try {
      const [ok, , json] = await secureFetch(endpoint.getSignedUrl, { 
        file_key: fileKey 
      });

      if (ok) {
        setState(prev => ({ ...prev, receiptUrl: json.preSignedUrl }));
        await fetchImage(json.preSignedUrl);
      } else {
        error("Failed to fetch receipt URL");
      }
    } catch (err) {
      error("Failed to fetch receipt image");
      console.error(err);
    }
  }, [fetchImage]);

  const fetchDriverReceiptDetails = useCallback(async (shouldFetchImage = false) => {
    setState(prev => ({ ...prev, fetching: true }));
    
    try {
      const response = await fetchWithJson(endpoint.dc_fetch_driver_receipt_detail, {
        receipt_id: currentReceipt.receipt_number
      });
      const data = await response.json();
      
      setState(prev => ({ 
        ...prev, 
        receiptsDetails: data, 
        comment: data.comments,
        fetching: false // Stop main spinner after API call completes
      }));

      // Only fetch image if requested (on initial load)
      if (shouldFetchImage && data.file_key) {
        await fetchReceiptImage(data.file_key);
      }

      return data; // Return data for other uses
    } catch (err) {
      error("Failed to fetch receipt details");
      console.error(err);
      setState(prev => ({ ...prev, fetching: false }));
      throw err;
    }
  }, [currentReceipt.receipt_number, fetchReceiptImage]);

  const handleApproved = useCallback(async () => {
    if (Number(state.amount) === 0 || !state.date) {
      alert(
        "Please click on Adopt driver or Adopt OCR button first. Or you may fill in the approval amounts and date manually. Then click on the approved button."
      );
      return;
    }

    try {
      const dtFormat = dayjs(state.date, "YYYY-MM-DD").format("DD-MMM-YYYY");
      
      await fetchWithJson(endpoint.dc_approve_receipt, {
        receipt_id: state.receiptsDetails.receipt_reference,
        amount: state.amount,
        date: state.date,
        comments: state.comment
      });

      success(RECEIPT_STATUS.APPROVED);
      setStatusOnGrid(
        state.receiptsDetails.receipt_reference, 
        RECEIPT_STATUS.APPROVED, 
        dtFormat, 
        state.amount
      );
      resetFilter();
      setState(prev => ({
        ...prev,
        receiptsDetails: {
          ...prev.receiptsDetails,
          status: RECEIPT_STATUS.APPROVED
        }
      }));
    } catch (err) {
      error("Failed to approve receipt");
      console.error(err);
    }
  }, [state.amount, state.date, state.receiptsDetails, state.comment, setStatusOnGrid, resetFilter]);

  const handleReject = useCallback(async () => {
    try {
      await fetchWithJson(endpoint.dc_reject_receipt, {
        receipt_id: state.receiptsDetails.receipt_reference,
        comments: state.comment
      });

      error(RECEIPT_STATUS.REJECTED);
      setStatusOnGrid(state.receiptsDetails.receipt_reference, RECEIPT_STATUS.REJECTED);
      resetFilter();
      setState(prev => ({
        ...prev,
        receiptsDetails: {
          ...prev.receiptsDetails,
          status: RECEIPT_STATUS.REJECTED
        }
      }));
    } catch (err) {
      error("Failed to reject receipt");
      console.error(err);
    }
  }, [state.receiptsDetails, state.comment, setStatusOnGrid, resetFilter]);

  const handleReset = useCallback(async () => {
    setState(prev => ({ ...prev, amount: 0, date: "" }));
    
    try {
      await fetchWithJson(endpoint.dc_reset_receipt, {
        receipt_id: state.receiptsDetails.receipt_reference
      });

      warning("Reset");
      setStatusOnGrid(state.receiptsDetails.receipt_reference, RECEIPT_STATUS.RESET);
      resetFilter();
      setState(prev => ({
        ...prev,
        receiptsDetails: {
          ...prev.receiptsDetails,
          status: RECEIPT_STATUS.RESET
        }
      }));
    } catch (err) {
      error("Failed to reset receipt status");
      console.error(err);
    }
  }, [state.receiptsDetails, setStatusOnGrid, resetFilter]);

  const handleSaveReceipt = useCallback(async () => {
    try {
      const dtFormat = dayjs(state.date, "YYYY-MM-DD").format("DD-MMM-YYYY");
      
      await fetchWithJson(endpoint.dc_save_receipt, {
        receipt_id: state.receiptsDetails.receipt_reference,
        amount: state.amount,
        date: state.date,
        comments: state.comment
      });

      success(RECEIPT_STATUS.SAVED);
      await fetchDriverReceiptDetails(false); // Only fetch receipt details, not image
      setStatusOnGrid(
        state.receiptsDetails.receipt_reference, 
        state.receiptsDetails.status, 
        dtFormat, 
        state.amount
      );
      resetFilter();
    } catch (err) {
      error("Failed to save receipt updates");
      console.error(err);
    }
  }, [
    state.date, 
    state.amount, 
    state.receiptsDetails, 
    state.comment, 
    fetchDriverReceiptDetails, 
    setStatusOnGrid, 
    resetFilter
  ]);

  const downloadImage = useCallback(() => {
    const filename = `receipt_file_${Math.random().toString(36).substr(2, 5)}.jpg`;
    const newBlob = new Blob([state.imageBlob], { type: "application/octet-stream" });

    if (typeof window.navigator.msSaveBlob !== "undefined") {
      window.navigator.msSaveBlob(newBlob, filename);
      return;
    }

    const blobURL = window.URL.createObjectURL(newBlob);
    const tempLink = document.createElement("a");
    tempLink.style.display = "none";
    tempLink.href = blobURL;
    tempLink.setAttribute("download", filename);

    if (typeof tempLink.download === "undefined") {
      tempLink.setAttribute("target", "_blank");
    }

    document.body.appendChild(tempLink);
    tempLink.click();
    document.body.removeChild(tempLink);
    
    setTimeout(() => {
      window.URL.revokeObjectURL(blobURL);
    }, 100);
  }, [state.imageBlob]);

  const adoptData = useCallback((type) => {
    const data = type === 'ocr' 
      ? {
          amount: state.receiptsDetails.ocr_total,
          date: state.receiptsDetails.ocr_date
            ? dayjs(state.receiptsDetails.ocr_date, "DD-MMM-YYYY").format("YYYY-MM-DD")
            : ""
        }
      : {
          amount: state.receiptsDetails.employee_total,
          date: dayjs(state.receiptsDetails.employee_date, "DD-MMM-YYYY").format("YYYY-MM-DD")
        };

    setState(prev => ({
      ...prev,
      adoptOCR: type === 'ocr',
      adoptDriver: type === 'driver',
      ...data
    }));
  }, [state.receiptsDetails]);

  useEffect(() => {
    // Fetch receipt details AND image on initial load
    fetchDriverReceiptDetails(true);
    
    return () => {
      if (state.localUrl) {
        URL.revokeObjectURL(state.localUrl);
      }
    };
  }, [fetchDriverReceiptDetails]);

  if (state.fetching) {
    return (
      <div className="flex justify-center mt6">
        <Loading size="lg">Fetching the receipt details..</Loading>
      </div>
    );
  }

  return (
    <div className="ml4 mt2 mr4 flex flex-row">
      <div className="w-3/5">
        <Card bordered shadow={false} css={{ w: "100%", br: 0 }}>
          <Card.Header>
            <Row justify="space-between">
              <Text 
                css={{
                  bc: `${state.receiptsDetails.status === RECEIPT_STATUS.REJECTED ? '$red100' : '$gray100'}`,
                  p: 10
                }}
                size="1.2rem" 
                b
              >
                {state.receiptsDetails.status}
              </Text>
              <Text 
                css={{
                  p: 10,
                  color: '$gray600'
                }}
                size="1.2rem" 
                weight="semibold"
              >
                Order: {state.receiptsDetails.order_id}, Stop Sequence: {state.receiptsDetails.order_stop_seq}
              </Text>
            </Row>
          </Card.Header>
          <Divider/>
          <Card.Body css={{ py: '$10' }}>
            <DataComparisonTable receiptsDetails={state.receiptsDetails} />
          </Card.Body>
          <Divider/>
          <Card.Footer>
            <Row justify="space-between" align="center">
              <Text
                size="1.2rem"
                css={{
                  p: 10,
                  color: '$gray700'
                }}
                weight="medium"
              >
                Driver: {state.receiptsDetails.driver}
              </Text>
              <div className="flex gap-2 items-center">
                <button
                  className="btn btn-primary btn-sm text-white"
                  onClick={() => adoptData('ocr')}
                >
                  Adopt OCR Data
                </button>
                <button
                  className="btn btn-secondary btn-sm text-white"
                  onClick={() => adoptData('driver')}
                >
                  Adopt Driver Data
                </button>
              </div>
            </Row>
          </Card.Footer>
        </Card>

        <div className="mt3 flex flex-wrap justify-end">
          <span className="text-xl py-6 font-semibold">Your Input</span>
          <div className="px-4">
            <label htmlFor="" className="f5 fw6" style={{width: '75px'}}>Amount</label>
            <div>
              <Input
                type='number'
                placeholder="Amount"
                onChange={(e) => handleInputChange('amount', e.target.value)}
                css={{ $$inputBorderRadius: "0", flexGrow: 1, h: 30, mt: 5 }}
                shadow={false}
                bordered
                rounded={false}
                value={state.amount}
              />
            </div>
          </div>

          <div>
            <label htmlFor="" className="f5 fw6">Date</label>
            <div>
              <Input
                type='date'
                placeholder="Date"
                onChange={(e) => handleInputChange('date', e.target.value)}
                css={{ $$inputBorderRadius: "0", flexGrow: 1, h: 30, mt: 5 }}
                shadow={false}
                bordered
                rounded={false}
                value={state.date}
              />
            </div>
          </div>

          <div className="w-100 mt3" style={{alignItems: 'center'}}>
            <label htmlFor="" className="f5 fw6" style={{width: '75px'}}>Comments</label>
            <div className="w-100 mt2 mb3">
              <Textarea 
                fullWidth
                bordered
                rows={4}
                css={{w: "100%", $$inputBorderRadius: "0"}}
                value={state.comment}
                onChange={(e) => {
                  if (e.target.value.length < 2000) {
                    handleInputChange('comment', e.target.value);
                  }
                }}
              />
            </div>
          </div>
        </div>

        <ActionButtons
          onApprove={handleApproved}
          onReject={handleReject}
          onReset={handleReset}
          onSave={handleSaveReceipt}
          onDownload={downloadImage}
          onClose={closeAction}
        />
      </div>
      <div className="ml4 bg-near-white w-2/5 flex justify-center items-center render-top">
        {state.imageLoading ? (
          <div className="flex flex-col items-center gap-3">
            <Loading size="lg" color="primary">
              Downloading receipt image...
            </Loading>
            <div className="text-sm text-gray-600 animate-pulse">
              Please wait while we fetch your receipt
            </div>
          </div>
        ) : (
          state.localUrl && <ReactImageZoom {...getZoomProps()} />
        )}
      </div>
    </div>
  );
};

export default ReimbursableReceiptsDetails;
